// Email Templates admin page — list + edit + duplicate + delete + create.
// Body editing now uses a proper WYSIWYG (Tiptap) so Paul + Sandra never
// touch raw HTML. The {{first_name}} and {{file:*}} tokens still flow
// through unchanged because we render them as plain text inside the
// editor — Tiptap preserves them verbatim through copy/paste/save.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import DOMPurify from "dompurify";
import RichTextEditor from "@/components/RichTextEditor";
import { CATEGORY_BUCKETS, groupTemplatesByBucket } from "@/lib/emailTemplateCategories";
import { resolveLandingTokens } from "@/lib/landingTokens";
import { DISPLAY_COLOR_OPTIONS, DisplayNamePill, displayColorClasses } from "@/lib/emailTemplateColors";
import {
  Loader2, Plus, Copy, Trash2, Save, X, Mail,
  Paperclip, FileText, ChevronRight, Search, AlertTriangle, CheckCircle2,
  Folder, FolderOpen, Home, ArrowLeft, Users, Globe, Lock,
  MousePointerClick, ExternalLink, Link2, Star,
} from "lucide-react";
import { toast } from "sonner";

export default function EmailTemplatesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email-templates");
      setItems(data.items || []);
      if (!selectedId && data.items?.length) setSelectedId(data.items[0].id);
    } finally { setLoading(false); }
  }, [selectedId]);
  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => items.find((t) => t.id === selectedId), [items, selectedId]);

  const createNew = async () => {
    const { data } = await api.post("/email-templates", { name: "New template", subject: "", body_html: "" });
    setItems((arr) => [data, ...arr]);
    setSelectedId(data.id);
  };
  const duplicate = async (id) => {
    const { data } = await api.post(`/email-templates/${id}/duplicate`);
    setItems((arr) => [data, ...arr]);
    setSelectedId(data.id);
    toast.success("Template duplicated");
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this template? This can't be undone.")) return;
    await api.delete(`/email-templates/${id}`);
    setItems((arr) => arr.filter((t) => t.id !== id));
    if (selectedId === id) setSelectedId(null);
    toast.success("Template deleted");
  };

  // Tick / untick the "Default template for this category" flag from
  // the list rail. The backend automatically demotes any sibling
  // template that previously held the flag inside the same category,
  // so we merge that demotion into local state manually to keep the
  // rail visually consistent without a full refetch.
  const toggleDefault = async (row) => {
    const next = !row.is_default;
    try {
      const { data } = await api.patch(`/email-templates/${row.id}`, { is_default: next });
      setItems((arr) => arr.map((t) => {
        if (t.id === data.id) return data;
        // When we're PROMOTING this row, demote every sibling that
        // shares the same category so the UI matches what the backend
        // just persisted. Uses the fresh category from ``data`` so a
        // simultaneous category change is respected.
        if (next && (t.category || null) === (data.category || null)) {
          return { ...t, is_default: false };
        }
        return t;
      }));
      toast.success(next
        ? (data.category ? `Set as default for ${data.category}` : "Set as default")
        : "Default cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update default");
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh] text-stone-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;

  return (
    <div className="flex h-[calc(100vh-4rem)]" data-testid="email-templates-page">
      {/* Left rail — list. Wider (w-96 = 24rem) so long template names
          + the coloured Display Name pill both fit without truncation. */}
      <div className="w-96 border-r border-stone-200 flex flex-col bg-stone-50">
        <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between">
          <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-700 flex items-center gap-1.5">
            <Mail className="w-3 h-3" /> Email Templates
          </h2>
          <button onClick={createNew} data-testid="template-new"
            className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-stone-950 hover:bg-stone-800 text-[#dddd16] rounded flex items-center gap-1">
            <Plus className="w-3 h-3" /> New
          </button>
        </div>
        <ul className="flex-1 overflow-y-auto">
          {items.length === 0 && <li className="px-4 py-6 text-xs text-stone-500 text-center">No templates yet.</li>}
          {/* Group templates into buckets driven by the free-text
              Category tag — cleaner scanning as the library grows. */}
          {(() => {
            const buckets = groupTemplatesByBucket(items);
            return CATEGORY_BUCKETS.map((b) => {
              const rows = buckets[b.id] || [];
              if (rows.length === 0) return null;
              return (
                <li key={b.id}>
                  <div className="px-4 pt-4 pb-1 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 bg-stone-100 border-b border-stone-200"
                       data-testid={`template-bucket-${b.id}`}>
                    {b.label}
                    <span className="ml-2 text-stone-400 font-normal">({rows.length})</span>
                  </div>
                  <ul>
                    {rows.map((t) => {
                      const active = t.id === selectedId;
                      return (
                        <li key={t.id} className="relative">
                          {/* Default toggle — absolute-positioned star so
                              it never fights with the row-select button
                              for click targets. Filled amber = default,
                              hollow stone = not default. Stops
                              propagation so tapping the star doesn't
                              also open the editor. */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleDefault(t); }}
                            title={t.is_default
                              ? "Default for this category — click to clear"
                              : "Set as default for this category"}
                            aria-pressed={t.is_default ? "true" : "false"}
                            data-testid={`template-default-toggle-${t.id}`}
                            className={`absolute top-2 right-2 z-10 w-6 h-6 flex items-center justify-center rounded-full border transition-colors ${
                              t.is_default
                                ? "bg-amber-100 border-amber-300 text-amber-600 hover:bg-amber-200"
                                : "bg-white border-stone-200 text-stone-300 hover:text-amber-500 hover:border-amber-300"
                            }`}
                          >
                            <Star className="w-3 h-3" fill={t.is_default ? "currentColor" : "none"} />
                          </button>
                          <button onClick={() => setSelectedId(t.id)}
                            data-testid={`template-row-${t.id}`}
                            className={`w-full text-left pl-4 pr-10 py-3 border-b border-stone-200 transition-colors ${active ? "bg-white" : "hover:bg-white"}`}>
                            {t.display_name && (
                              <div className="mb-1.5">
                                <DisplayNamePill displayName={t.display_name} color={t.display_color} />
                              </div>
                            )}
                            <div className="text-sm font-semibold text-stone-900 truncate">{t.name}</div>
                            <div className="text-[11px] text-stone-500 truncate mt-0.5">{t.subject || "— no subject —"}</div>
                            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                              {t.category && (
                                <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold bg-stone-200 text-stone-700 rounded">
                                  {t.category}
                                </span>
                              )}
                              {t.is_default && (
                                <span
                                  data-testid={`template-default-badge-${t.id}`}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold bg-amber-500 text-white rounded"
                                >
                                  <Star className="w-2.5 h-2.5" fill="currentColor" /> Default
                                </span>
                              )}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            });
          })()}
        </ul>
      </div>

      {/* Right pane — editor or empty state */}
      <div className="flex-1 overflow-y-auto">
        {!selected ? (
          <div className="flex items-center justify-center h-full text-stone-500">Select a template, or click "New".</div>
        ) : (
          <TemplateEditor
            key={selected.id}
            template={selected}
            onChanged={(patched) => setItems((arr) => arr.map((t) => t.id === patched.id ? patched : t))}
            onDuplicate={() => duplicate(selected.id)}
            onDelete={() => remove(selected.id)}
          />
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// TemplateEditor — one selected template. Lazy save: dirty flag + manual
// Save button to keep the wire-traffic low (these are long HTML bodies).
// ---------------------------------------------------------------------------
function TemplateEditor({ template, onChanged, onDuplicate, onDelete }) {
  const [draft, setDraft] = useState(template);
  const [saving, setSaving] = useState(false);
  const [showFilePicker, setShowFilePicker] = useState(false);
  // When the file picker is opened from a CTA toolbar button we capture
  // which button-style ("cta" / "outline") was clicked, so the chosen
  // R2 file becomes an inline yellow or outline button.
  const [filePickerMode, setFilePickerMode] = useState("cta");
  // Label the admin typed BEFORE the file picker opens — preserved here
  // so we use *their* wording on the button, not the filename.
  const [pendingLabel, setPendingLabel] = useState("");
  // Live Tiptap editor instance — exposed via RichTextEditor's `onReady`
  // callback so we can call `editor.chain().insertContent(...)` and have
  // the new node land at the actual cursor position instead of always
  // being appended to the end of the body.
  const editorRef = useRef(null);
  // Available signatures loaded from the backend — populates the
  // "Signature" picker so admins can pin a template to either Paul or
  // Sandra's sign-off. Fetched once per editor mount; adding a signer
  // server-side surfaces it here without a frontend deploy.
  const [signatures, setSignatures] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/email-templates/signatures/available");
        if (!cancelled) setSignatures(data?.items || []);
      } catch { /* fall back to hard-coded default */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { setDraft(template); }, [template]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(template);
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const setCsv = (k) => (e) =>
    setDraft((d) => ({ ...d, [k]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/email-templates/${template.id}`, draft);
      onChanged(data);
      // Re-sync local draft so the preview pulls the freshly built
      // ``rendered_html`` (editable body + system signature).
      setDraft(data);
      toast.success("Template saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  // Insert a snippet at the live Tiptap cursor. Falls back to string
  // append if the editor isn't ready yet (shouldn't happen — the
  // toolbar lives inside RichTextEditor itself).
  const insertAtCursor = (snippet) => {
    const ed = editorRef.current;
    if (ed) {
      // `focus()` restores the last cursor position even if the editor
      // lost focus while a modal/prompt was open.
      ed.chain().focus().insertContent(snippet).run();
      return;
    }
    setDraft((d) => ({ ...d, body_html: (d.body_html || "") + snippet }));
  };

  const insertFirstName = () => insertAtCursor("{{first_name}}");

  // Open the unified in-app CTA composer (replaces the old 3 stacked
  // window.prompt/confirm dialogs). The modal handles URL / R2 file /
  // Landing Page source selection itself and calls back via the
  // appropriate insertion path.
  const [showCtaModal, setShowCtaModal] = useState(false);
  const [ctaStyle, setCtaStyle] = useState("cta");

  const openCtaModal = (style) => {
    setCtaStyle(style);
    setShowCtaModal(true);
  };
  const insertCta = () => openCtaModal("cta");
  const insertOutline = () => openCtaModal("outline");

  const buildButtonHtml = ({ text, url, style }) => {
    const cls = style === "outline" ? "cm-btn-outline" : "cm-btn-cta";
    return `<p><a href="${url}" class="${cls}" target="_blank" rel="noopener noreferrer">${text}</a></p>`;
  };

  // Called by the new CTA modal once the admin has picked source + label
  // + target. Performs side-effects (attaching file metadata to draft if
  // an R2 file was chosen) then inserts the styled button at the cursor.
  const insertCtaButton = ({ label, source, url, file, landingSlug, style }) => {
    if (source === "file" && file) {
      const slug = (file.name || "file").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || `file_${Math.random().toString(36).slice(2, 7)}`;
      setDraft((d) => {
        const existing = d.attachments || [];
        const dedup = [...existing.filter((a) => a.placeholder !== slug),
          { key: file.key, name: file.name, placeholder: slug }];
        return { ...d, attachments: dedup };
      });
      insertAtCursor(buildButtonHtml({ text: label, url: `{{file:${slug}}}`, style }));
      return;
    }
    if (source === "landing" && landingSlug) {
      insertAtCursor(buildButtonHtml({ text: label, url: `{{landing:${landingSlug}}}`, style }));
      return;
    }
    if (source === "url" && url) {
      const normalised = /^https?:\/\//i.test(url) || url.startsWith("mailto:") || url.startsWith("{{") ? url : `https://${url}`;
      insertAtCursor(buildButtonHtml({ text: label, url: normalised, style }));
    }
  };

  // Legacy onPickFile retained for the standalone "Insert R2 file link"
  // paperclip button at the top of the editor — uses filename as label.
  const onPickFile = (f) => {
    const slug = (f.name || "file").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || `file_${Math.random().toString(36).slice(2, 7)}`;
    setDraft((d) => {
      const existing = d.attachments || [];
      const dedup = [...existing.filter((a) => a.placeholder !== slug),
        { key: f.key, name: f.name, placeholder: slug }];
      return { ...d, attachments: dedup };
    });
    const cls = filePickerMode === "outline" ? "cm-btn-outline" : "cm-btn-cta";
    const label = (pendingLabel || "").trim() || (filePickerMode === "outline"
      ? `Watch ${f.name}`
      : `Click here to download ${f.name}`);
    insertAtCursor(`<p><a href="{{file:${slug}}}" class="${cls}" target="_blank" rel="noopener noreferrer">${label}</a></p>`);
    setShowFilePicker(false);
    setFilePickerMode("cta");
    setPendingLabel("");
  };

  return (
    <div className="p-6 max-w-4xl space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0 space-y-1">
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600">
            Template name <span className="text-stone-400 normal-case font-normal tracking-normal">— shown in the &ldquo;Reply with template&rdquo; dropdown</span>
          </label>
          <input
            value={draft.name || ""}
            onChange={set("name")}
            placeholder="e.g. Franchise Enquiry Reply 2026"
            data-testid="template-name"
            className="text-xl font-bold text-stone-950 w-full bg-stone-50 border border-stone-300 rounded-lg px-3 py-2 focus:outline-none focus:border-stone-900"
          />
          <div className="text-xs text-stone-500">
            {template.updated_at && <>Last saved {new Date(template.updated_at).toLocaleString("en-GB")}{template.updated_by ? ` · ${template.updated_by}` : ""}</>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={onDuplicate} data-testid="template-duplicate" className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-stone-300 hover:bg-stone-50 rounded-lg flex items-center gap-1">
            <Copy className="w-3 h-3" /> Duplicate
          </button>
          <button onClick={onDelete} data-testid="template-delete" className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-red-300 text-red-700 hover:bg-red-50 rounded-lg flex items-center gap-1">
            <Trash2 className="w-3 h-3" /> Delete
          </button>
          {dirty && (
            <button onClick={save} disabled={saving} data-testid="template-save"
              className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-[#dddd16] hover:bg-[#aaaa11] text-stone-950 rounded-lg flex items-center gap-1 disabled:opacity-50">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </button>
          )}
        </div>
      </div>

      {/* Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1">Subject</label>
          <input
            value={draft.subject || ""}
            onChange={set("subject")}
            placeholder="Your Creative Mojo Franchise Enquiry"
            data-testid="template-subject"
            className="w-full px-3 py-2 bg-white border border-stone-300 text-sm rounded-lg focus:outline-none focus:border-stone-900"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1">From (sender email)</label>
          <input value={draft.default_from || ""} onChange={set("default_from")} placeholder="paul@creativemojo.co.uk" data-testid="template-from"
            className="w-full px-3 py-2 bg-white border border-stone-300 text-sm rounded-lg focus:outline-none focus:border-stone-900" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1">Sender display name</label>
          <input value={draft.sender_name || ""} onChange={set("sender_name")} placeholder="Paul Caldeira-Dunkerley" data-testid="template-sender-name"
            className="w-full px-3 py-2 bg-white border border-stone-300 text-sm rounded-lg focus:outline-none focus:border-stone-900" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1">
            Signature <span className="text-stone-400 normal-case font-normal tracking-normal">— which sign-off gets appended when this template sends</span>
          </label>
          <div className="flex flex-wrap gap-2" data-testid="template-signature-picker">
            {(signatures.length ? signatures : [
              { key: "paul", label: "Paul Caldeira-Dunkerley", email: "paul@creativemojo.co.uk" },
              { key: "sandra", label: "Sandra Caldeira-Dunkerley", email: "sandra@creativemojo.co.uk" },
            ]).map((sig) => {
              const active = (draft.signature_key || "paul") === sig.key;
              return (
                <button
                  key={sig.key}
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, signature_key: sig.key }))}
                  data-testid={`template-signature-${sig.key}`}
                  className={`px-3 py-2 text-xs rounded-lg border transition-colors text-left ${
                    active
                      ? "bg-stone-950 text-[#dddd16] border-stone-950 font-bold"
                      : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
                  }`}
                >
                  <div className="font-semibold">{sig.label}</div>
                  {sig.email && (
                    <div className={`text-[10px] normal-case tracking-normal ${active ? "text-stone-400" : "text-stone-500"}`}>{sig.email}</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1">Default Cc (comma separated)</label>
          <input value={(draft.default_cc || []).join(", ")} onChange={setCsv("default_cc")} data-testid="template-cc"
            className="w-full px-3 py-2 bg-white border border-stone-300 text-sm rounded-lg focus:outline-none focus:border-stone-900" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1">Default Bcc (comma separated)</label>
          <input value={(draft.default_bcc || []).join(", ")} onChange={setCsv("default_bcc")} data-testid="template-bcc"
            className="w-full px-3 py-2 bg-white border border-stone-300 text-sm rounded-lg focus:outline-none focus:border-stone-900" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1">Category (optional tag)</label>
          <input value={draft.category || ""} onChange={set("category")} placeholder="franchise / licence / shadow-day" data-testid="template-category"
            className="w-full px-3 py-2 bg-white border border-stone-300 text-sm rounded-lg focus:outline-none focus:border-stone-900" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1">
            Display name <span className="text-stone-400 normal-case font-normal tracking-normal">— short tag shown in the &ldquo;Reply with template&rdquo; dropdown</span>
          </label>
          <input
            value={draft.display_name || ""}
            onChange={set("display_name")}
            placeholder="BLANK INTRO / AREA TAKEN / FOLLOW UP"
            data-testid="template-display-name"
            className="w-full px-3 py-2 bg-white border border-stone-300 text-sm rounded-lg focus:outline-none focus:border-stone-900 uppercase tracking-wider font-bold text-stone-900 placeholder:font-normal placeholder:tracking-normal placeholder:text-stone-400 placeholder:normal-case" />
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] text-stone-500 mr-1">Colour tag:</span>
            <button
              type="button"
              onClick={() => setDraft((d) => ({ ...d, display_color: null }))}
              title="No colour — neutral chip"
              data-testid="template-display-color-none"
              className={`w-6 h-6 rounded-full border-2 bg-stone-200 border-stone-400 ${!draft.display_color ? "ring-2 ring-offset-1 ring-stone-900" : "hover:ring-1 hover:ring-stone-400"}`}>
              <span className="sr-only">No colour</span>
            </button>
            {DISPLAY_COLOR_OPTIONS.map((opt) => {
              const c = displayColorClasses(opt.value);
              const active = draft.display_color === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, display_color: opt.value }))}
                  title={opt.label}
                  data-testid={`template-display-color-${opt.value}`}
                  className={`w-6 h-6 rounded-full border-2 ${c.bg} ${c.border} ${active ? "ring-2 ring-offset-1 ring-stone-900" : "hover:ring-1 hover:ring-stone-400"}`}>
                  <span className="sr-only">{opt.label}</span>
                </button>
              );
            })}
            {(draft.display_name || draft.display_color) && (
              <div className="ml-2 flex items-center gap-1.5">
                <span className="text-[10px] text-stone-500">Preview:</span>
                <DisplayNamePill
                  displayName={draft.display_name || "TAG"}
                  color={draft.display_color} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Body — WYSIWYG canvas matches the final email layout exactly:
          logo at the top, your editable body, and the locked Creative
          Mojo signature beneath. No more "Preview" toggle — what you
          see is what gets sent. */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600">Email body (live preview)</label>
          <div className="flex items-center gap-2">
            <button type="button" onClick={insertFirstName} data-testid="template-insert-firstname"
              className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-stone-100 hover:bg-stone-200 text-stone-800 rounded inline-flex items-center gap-1">
              + {`{{first_name}}`}
            </button>
            <button type="button" onClick={() => { setFilePickerMode("cta"); setShowFilePicker(true); }} data-testid="template-insert-file"
              className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-stone-100 hover:bg-stone-200 text-stone-800 rounded inline-flex items-center gap-1">
              <Paperclip className="w-3 h-3" /> Insert R2 file link
            </button>
          </div>
        </div>
        <RichTextEditor
          value={draft.body_html || ""}
          onChange={(html) => setDraft((d) => ({ ...d, body_html: html }))}
          placeholder="Write your email here. Use the toolbar — bold, CTA buttons, lists, links."
          testIdPrefix="template-body"
          onInsertCta={insertCta}
          onInsertOutline={insertOutline}
          onReady={(ed) => { editorRef.current = ed; }}
          signatureHtml={
            // Prefer the freshly-picked signature from the loaded list
            // so switching the radio updates the preview instantly.
            // Falls back to the backend-serialised copy for legacy
            // templates that pre-date the picker.
            (signatures.find((s) => s.key === (draft.signature_key || "paul"))?.preview_html)
              || draft.signature_html
              || ""
          }
          logoUrl="/brand/creative-mojo-logo.png"
        />
      </div>

      {/* Attached files */}
      {(draft.attachments || []).length > 0 && (
        <div className="border border-stone-200 rounded-lg p-3 bg-stone-50">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-2">Linked files</div>
          <ul className="space-y-1.5">
            {draft.attachments.map((a, i) => (
              <li key={`${a.placeholder}-${i}`} className="flex items-center gap-2 text-xs">
                <FileText className="w-3 h-3 text-stone-400 shrink-0" />
                <span className="font-mono text-stone-700 truncate flex-1">{a.placeholder ? `{{file:${a.placeholder}}}` : "(no placeholder)"}</span>
                <span className="text-stone-500 truncate">→ {a.name}</span>
                <span className="text-stone-400 truncate text-[10px]">{a.key || "(no R2 key set — pick a file)"}</span>
                <button type="button" onClick={() => setDraft((d) => ({ ...d, attachments: (d.attachments || []).filter((_, idx) => idx !== i) }))}
                  data-testid={`template-attach-remove-${i}`}
                  className="text-stone-400 hover:text-red-700">
                  <X className="w-3 h-3" />
                </button>
              </li>
            ))}
          </ul>
          {draft.attachments.some((a) => !a.key) && (
            <div className="mt-2 text-[11px] text-amber-800 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Some files don't have an R2 key set — click "Insert R2 file link" to pick from your storage and overwrite the placeholder.
            </div>
          )}
        </div>
      )}

      {showFilePicker && <FilePickerModal onClose={() => setShowFilePicker(false)} onPick={onPickFile} />}
      {showCtaModal && (
        <CtaComposerModal
          style={ctaStyle}
          onClose={() => setShowCtaModal(false)}
          onInsert={(payload) => { setShowCtaModal(false); insertCtaButton({ ...payload, style: ctaStyle }); }}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// CtaComposerModal — single in-app dialog that replaces the previous
// trio of native window.prompt / window.confirm dialogs. Lets admins
// type a label, pick a source (external URL, R2 file, Landing Page),
// and insert the styled button at the cursor in one go.
// ---------------------------------------------------------------------------
function CtaComposerModal({ style, onClose, onInsert }) {
  const [label, setLabel] = useState(style === "outline" ? "Watch the video" : "Download the Info Pack");
  const [source, setSource] = useState("landing"); // "landing" | "file" | "url"
  const [url, setUrl] = useState("https://");
  const [file, setFile] = useState(null); // {key, name}
  const [landingSlug, setLandingSlug] = useState("");
  const [landingPages, setLandingPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [showFilePicker, setShowFilePicker] = useState(false);

  // Lazy-load landing pages once on mount.
  useEffect(() => {
    setLoadingPages(true);
    api.get("/admin/landing-pages").then(({ data }) => {
      const active = (data.items || []).filter((p) => p.active);
      setLandingPages(active);
      if (active.length > 0) setLandingSlug(active[0].slug);
      else if (source === "landing") setSource("file"); // graceful fallback
    }).catch(() => { /* ignore */ }).finally(() => setLoadingPages(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const canInsert = label.trim() && (
    (source === "url" && url.trim() && url.trim() !== "https://") ||
    (source === "file" && file) ||
    (source === "landing" && landingSlug)
  );

  const submit = () => {
    if (!canInsert) return;
    onInsert({
      label: label.trim(),
      source,
      url: source === "url" ? url.trim() : undefined,
      file: source === "file" ? file : undefined,
      landingSlug: source === "landing" ? landingSlug : undefined,
    });
  };

  const styleLabel = style === "outline" ? "Outline button" : "Yellow CTA button";

  return (
    <div onClick={onClose} className="fixed inset-0 z-[70] bg-stone-950/60 backdrop-blur-sm flex items-center justify-center p-6" data-testid="cta-composer-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
          <h3 className="font-bold text-stone-900 flex items-center gap-2">
            <MousePointerClick className="w-4 h-4" /> Insert {styleLabel}
          </h3>
          <button onClick={onClose} className="w-9 h-9 hover:bg-stone-100 rounded-lg flex items-center justify-center" data-testid="cta-composer-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Label */}
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1">Button label</div>
            <input value={label} onChange={(e) => setLabel(e.target.value)} autoFocus data-testid="cta-composer-label"
              className="w-full px-3 py-2 bg-stone-50 border border-stone-300 text-sm rounded-lg focus:outline-none focus:border-stone-900" />
          </label>

          {/* Source toggle */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1.5">Link to</div>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { key: "landing", label: "Landing Page", icon: ExternalLink },
                { key: "file",    label: "R2 File",      icon: Paperclip },
                { key: "url",     label: "External URL", icon: Link2 },
              ].map(({ key, label: l, icon: Ic }) => (
                <button key={key} type="button" onClick={() => setSource(key)} data-testid={`cta-composer-source-${key}`}
                  className={`px-2 py-2 text-[11px] font-semibold uppercase tracking-wider rounded-lg border-2 inline-flex flex-col items-center gap-1 transition-colors ${
                    source === key ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-200 text-stone-700 hover:bg-stone-50"
                  }`}>
                  <Ic className="w-3.5 h-3.5" /> {l}
                </button>
              ))}
            </div>
          </div>

          {/* Conditional inputs */}
          {source === "url" && (
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1">URL</div>
              <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="cta-composer-url"
                placeholder="https://…"
                className="w-full px-3 py-2 bg-stone-50 border border-stone-300 text-sm rounded-lg font-mono focus:outline-none focus:border-stone-900" />
            </label>
          )}

          {source === "file" && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1">R2 File</div>
              {file ? (
                <div className="flex items-center gap-2 bg-stone-50 border border-stone-300 rounded-lg p-2.5">
                  <FileText className="w-4 h-4 text-stone-400 shrink-0" />
                  <div className="flex-1 min-w-0 text-xs">
                    <div className="text-stone-900 truncate">{file.name}</div>
                    <div className="text-stone-500 truncate font-mono text-[10px]">{file.key}</div>
                  </div>
                  <button type="button" onClick={() => setShowFilePicker(true)} data-testid="cta-composer-file-change"
                    className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-stone-100 hover:bg-stone-200 rounded">Change</button>
                </div>
              ) : (
                <button type="button" onClick={() => setShowFilePicker(true)} data-testid="cta-composer-file-pick"
                  className="w-full px-3 py-2.5 border border-dashed border-stone-300 hover:border-stone-500 text-sm text-stone-600 hover:text-stone-900 rounded-lg inline-flex items-center justify-center gap-2 transition-colors">
                  <Paperclip className="w-4 h-4" /> Pick a file from R2
                </button>
              )}
            </div>
          )}

          {source === "landing" && (
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1">Landing Page</div>
              {loadingPages ? (
                <div className="px-3 py-2 text-xs text-stone-500"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading pages…</div>
              ) : landingPages.length === 0 ? (
                <div className="px-3 py-2.5 text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-lg">
                  No active landing pages — create one at <a href="/admin/landing-pages" target="_blank" rel="noopener noreferrer" className="underline">/admin/landing-pages</a>, then come back.
                </div>
              ) : (
                <select value={landingSlug} onChange={(e) => setLandingSlug(e.target.value)} data-testid="cta-composer-landing"
                  className="w-full px-3 py-2 bg-stone-50 border border-stone-300 text-sm rounded-lg focus:outline-none focus:border-stone-900">
                  {landingPages.map((p) => (
                    <option key={p.id} value={p.slug}>{p.title} ({`/info/${p.slug}`})</option>
                  ))}
                </select>
              )}
            </label>
          )}
        </div>

        <div className="px-5 py-3 border-t border-stone-200 bg-stone-50 flex items-center justify-end gap-2">
          <button onClick={onClose} data-testid="cta-composer-cancel"
            className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-white border border-stone-200 hover:bg-stone-100 text-stone-800 rounded">Cancel</button>
          <button onClick={submit} disabled={!canInsert} data-testid="cta-composer-insert"
            className="px-4 py-1.5 text-xs font-bold uppercase tracking-wider bg-stone-900 hover:bg-stone-800 disabled:bg-stone-300 disabled:cursor-not-allowed text-white rounded inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Insert button
          </button>
        </div>

        {showFilePicker && (
          <FilePickerModal
            onClose={() => setShowFilePicker(false)}
            onPick={(f) => { setFile({ key: f.key, name: f.name }); setShowFilePicker(false); }}
          />
        )}
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// PreviewHtml — renders the template body with {{first_name}} replaced
// and {{file:*}} links shown as friendly chip text.
//
// We wrap the rendered HTML in a div whose CSS gives `<p>` real
// margin-bottom so empty paragraphs (a single Enter-Enter blank line)
// show visible vertical space — matching how Gmail/Outlook will render
// the same markup. Without this the preview collapses every paragraph
// flush against the next, which doesn't reflect the actual email.
// ---------------------------------------------------------------------------
function PreviewHtml({ html, sampleFirstName }) {
  const [landingResolvedHtml, setLandingResolvedHtml] = useState(null);
  const baseRendered = useMemo(() => {
    let h = html || "";
    h = h.replace(/\{\{\s*first_name\s*\}\}/g, sampleFirstName);
    // {{file:*}} placeholders → fake share URL so the styled button renders.
    h = h.replace(/\{\{\s*file:([^}]+)\s*\}\}/g, (_, slug) => `#preview-${slug}`);
    return h;
  }, [html, sampleFirstName]);

  // Resolve {{landing:*}} tokens asynchronously — SAME resolver the
  // Resend send pipeline uses at send time. Ensures the admin preview
  // shows the real destination URL (or a red warning if the slug
  // doesn't resolve) instead of the raw token, which the browser
  // otherwise treats as a relative URL and rewrites to /admin/%7B%7B...
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out = await resolveLandingTokens(baseRendered);
      if (!cancelled) setLandingResolvedHtml(out);
    })();
    return () => { cancelled = true; };
  }, [baseRendered]);

  const rendered = landingResolvedHtml ?? baseRendered;
  // eslint-disable-next-line react/no-danger
  return (
    <div
      className="email-preview-body text-stone-900"
      // Inline styles so we don't fight Tailwind's preflight on `<p>`.
      style={{ fontFamily: "Helvetica, Arial, sans-serif", lineHeight: 1.55, fontSize: 15 }}
    >
      <style>{`
        .email-preview-body p { margin: 0 0 14px 0; min-height: 1em; }
        .email-preview-body p:last-child { margin-bottom: 0; }
        .email-preview-body h1 { font-size: 22px; font-weight: 700; margin: 18px 0 10px 0; }
        .email-preview-body h2 { font-size: 18px; font-weight: 700; margin: 16px 0 8px 0; }
        .email-preview-body h3 { font-size: 16px; font-weight: 600; margin: 14px 0 6px 0; }
        .email-preview-body ul, .email-preview-body ol { margin: 0 0 14px 22px; }
        .email-preview-body li { margin: 4px 0; }
        .email-preview-body a { color: #1a1a1a; text-decoration: underline; }
      `}</style>
      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rendered, { ADD_ATTR: ["target"] }) }} />
    </div>
  );
}


// ---------------------------------------------------------------------------
// FilePickerModal — Search + Browse the entire R2 index (admin scope).
// Search mode: /api/files/search?q=
// Browse mode: /api/files/tree?prefix=  with breadcrumb navigation, so admins
// can drill into private folders like admin/franchise-sales-pdf/ that get
// buried in search results.
// ---------------------------------------------------------------------------
function FilePickerModal({ onClose, onPick }) {
  const [tab, setTab] = useState("browse"); // "browse" | "search"
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [err, setErr] = useState("");

  // Browse state
  const [prefix, setPrefix] = useState(""); // current folder prefix
  const [tree, setTree] = useState({ folders: [], files: [] });
  const [browsing, setBrowsing] = useState(false);

  // Debounced search
  useEffect(() => {
    if (tab !== "search") return;
    if ((q || "").length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true); setErr("");
      try {
        const { data } = await api.get("/files/search", { params: { q, limit: 60 } });
        setResults(data.items || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Search failed");
      } finally { setSearching(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, tab]);

  // Load tree for current prefix
  useEffect(() => {
    if (tab !== "browse") return;
    let cancelled = false;
    (async () => {
      setBrowsing(true); setErr("");
      try {
        const { data } = await api.get("/files/tree", { params: { prefix } });
        if (!cancelled) setTree({ folders: data.folders || [], files: data.files || [] });
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Could not load folder");
      } finally { if (!cancelled) setBrowsing(false); }
    })();
    return () => { cancelled = true; };
  }, [prefix, tab]);

  // Breadcrumbs derived from current prefix
  const crumbs = useMemo(() => {
    if (!prefix) return [];
    const parts = prefix.replace(/\/$/, "").split("/");
    return parts.map((seg, i) => ({
      name: seg,
      prefix: parts.slice(0, i + 1).join("/") + "/",
    }));
  }, [prefix]);

  // Icon for a top-level folder name (only at root)
  const rootIcon = (name) => {
    if (name === "admin") return <Lock className="w-4 h-4 text-orange-500 shrink-0" />;
    if (name === "shared") return <Globe className="w-4 h-4 text-emerald-600 shrink-0" />;
    if (name === "franchisees") return <Users className="w-4 h-4 text-stone-600 shrink-0" />;
    return <Folder className="w-4 h-4 text-stone-400 shrink-0" />;
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-[80] bg-stone-950/60 backdrop-blur-sm flex items-center justify-center p-6" data-testid="file-picker-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] flex flex-col shadow-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
          <h3 className="font-bold text-stone-900 flex items-center gap-2"><Paperclip className="w-4 h-4" /> Pick a file from R2</h3>
          <button onClick={onClose} className="w-9 h-9 hover:bg-stone-100 rounded-lg flex items-center justify-center" data-testid="file-picker-close"><X className="w-4 h-4" /></button>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-3 border-b border-stone-200 flex items-center gap-1">
          <button
            onClick={() => setTab("browse")}
            data-testid="file-picker-tab-browse"
            className={`px-3 py-1.5 text-xs font-semibold rounded-t-md border-b-2 transition-colors ${tab === "browse" ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-800"}`}>
            <FolderOpen className="w-3.5 h-3.5 inline mr-1.5" />Browse
          </button>
          <button
            onClick={() => setTab("search")}
            data-testid="file-picker-tab-search"
            className={`px-3 py-1.5 text-xs font-semibold rounded-t-md border-b-2 transition-colors ${tab === "search" ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-800"}`}>
            <Search className="w-3.5 h-3.5 inline mr-1.5" />Search
          </button>
        </div>

        {tab === "search" && (
          <div className="px-5 py-3 border-b border-stone-200">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus
                placeholder="Search files by name (try .pdf)…" data-testid="file-picker-search"
                className="w-full pl-9 pr-3 py-2 bg-stone-50 border border-stone-300 text-sm rounded-lg focus:outline-none focus:border-stone-900" />
            </div>
          </div>
        )}

        {tab === "browse" && (
          <div className="px-5 py-2.5 border-b border-stone-200 flex items-center gap-1.5 text-xs overflow-x-auto">
            <button
              onClick={() => setPrefix("")}
              data-testid="file-picker-crumb-root"
              className={`px-2 py-1 rounded flex items-center gap-1 shrink-0 ${prefix === "" ? "bg-stone-900 text-white" : "hover:bg-stone-100 text-stone-700"}`}>
              <Home className="w-3 h-3" /> Root
            </button>
            {crumbs.map((c, i) => (
              <div key={c.prefix} className="flex items-center gap-1 shrink-0">
                <ChevronRight className="w-3 h-3 text-stone-400" />
                <button
                  onClick={() => setPrefix(c.prefix)}
                  data-testid={`file-picker-crumb-${i}`}
                  className={`px-2 py-1 rounded ${i === crumbs.length - 1 ? "bg-stone-900 text-white" : "hover:bg-stone-100 text-stone-700"}`}>
                  {c.name}
                </button>
              </div>
            ))}
            {prefix && (
              <button
                onClick={() => {
                  const parts = prefix.replace(/\/$/, "").split("/");
                  parts.pop();
                  setPrefix(parts.length ? parts.join("/") + "/" : "");
                }}
                data-testid="file-picker-up"
                className="ml-auto px-2 py-1 rounded hover:bg-stone-100 text-stone-700 flex items-center gap-1 shrink-0">
                <ArrowLeft className="w-3 h-3" /> Up
              </button>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {err && <div className="p-4 text-red-700 bg-red-50 text-sm">{err}</div>}

          {tab === "search" && (
            <>
              {searching && <div className="p-6 text-center text-stone-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Searching…</div>}
              {!searching && !err && q.length >= 2 && results.length === 0 && (
                <div className="p-6 text-center text-stone-500 text-sm">No files match — try a different search or switch to Browse.</div>
              )}
              {!searching && !err && q.length < 2 && (
                <div className="p-6 text-center text-stone-500 text-sm">Type at least 2 characters to search.</div>
              )}
              <ul className="divide-y divide-stone-100">
                {results.map((f) => (
                  <li key={f.key}>
                    <button onClick={() => onPick(f)} data-testid={`file-pick-${f.key.replace(/[^a-z0-9]+/gi, "-")}`}
                      className="w-full text-left px-5 py-2.5 hover:bg-stone-50 flex items-center gap-3">
                      <FileText className="w-4 h-4 text-stone-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-stone-900 truncate">{f.name}</div>
                        <div className="text-[10px] text-stone-500 truncate font-mono">{f.key}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-stone-400 shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {tab === "browse" && (
            <>
              {browsing && <div className="p-6 text-center text-stone-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>}
              {!browsing && !err && tree.folders.length === 0 && tree.files.length === 0 && (
                <div className="p-6 text-center text-stone-500 text-sm">This folder is empty.</div>
              )}
              <ul className="divide-y divide-stone-100">
                {tree.folders.map((d) => (
                  <li key={d.key}>
                    <button
                      onClick={() => setPrefix(d.key)}
                      data-testid={`file-picker-folder-${d.name.replace(/[^a-z0-9]+/gi, "-")}`}
                      className="w-full text-left px-5 py-2.5 hover:bg-stone-50 flex items-center gap-3">
                      {prefix === "" ? rootIcon(d.name) : <Folder className="w-4 h-4 text-stone-400 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-stone-900 truncate font-medium">{d.name}</div>
                        <div className="text-[10px] text-stone-500 truncate">{d.files} file{d.files === 1 ? "" : "s"}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-stone-400 shrink-0" />
                    </button>
                  </li>
                ))}
                {tree.files.map((f) => (
                  <li key={f.key}>
                    <button onClick={() => onPick(f)} data-testid={`file-pick-${f.key.replace(/[^a-z0-9]+/gi, "-")}`}
                      className="w-full text-left px-5 py-2.5 hover:bg-stone-50 flex items-center gap-3">
                      <FileText className="w-4 h-4 text-stone-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-stone-900 truncate">{f.name}</div>
                        <div className="text-[10px] text-stone-500 truncate font-mono">{f.key}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-stone-400 shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-stone-200 bg-stone-50 text-[11px] text-stone-500">
          Picked file's R2 key + name will be stored on the template. A fresh signed URL is minted at send time.
        </div>
      </div>
    </div>
  );
}
